import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  AdminPanelSettings as AdminIcon,
  Key as KeyIcon,
  AccountCircle as AccountIcon
} from '@mui/icons-material';
import apiService from '../../services/api';
import MFASetup from './MFASetup';
import { isPasswordValid } from '../../utils/passwordValidation';
import PasswordStrengthIndicator from '../common/PasswordStrengthIndicator';

// Shared input styles matching the application theme
const inputStyles = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: '6px',
    fontFamily: 'Metropolis, sans-serif',
    '& fieldset': {
      borderColor: '#E5E5E5',
    },
    '&:hover fieldset': {
      borderColor: '#D9DBDC',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4698D3',
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': {
      color: '#4698D3',
    },
  },
  '& .MuiInputBase-input': {
    color: '#253E66',
    fontFamily: 'Metropolis, sans-serif',
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#999999',
  },
};

// TOTP Input Component
const TOTPInput = ({ value, onChange, disabled }) => {
  const inputRefs = useRef([]);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);

  useEffect(() => {
    const digitsArray = value.split('').slice(0, 6);
    while (digitsArray.length < 6) {
      digitsArray.push('');
    }
    setDigits(digitsArray);
  }, [value]);

  const handleChange = (index, newValue) => {
    if (newValue.length > 1) {
      newValue = newValue.slice(-1);
    }

    if (!/^\d*$/.test(newValue)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = newValue;
    setDigits(newDigits);

    const totpValue = newDigits.join('');
    onChange(totpValue);

    if (newValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const pastedDigits = pastedData.replace(/\D/g, '').slice(0, 6);

    if (pastedDigits.length > 0) {
      const newDigits = [...digits];
      for (let i = 0; i < pastedDigits.length && i < 6; i++) {
        newDigits[i] = pastedDigits[i];
      }
      setDigits(newDigits);
      onChange(pastedDigits);

      const nextIndex = Math.min(pastedDigits.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
      {digits.map((digit, index) => (
        <TextField
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              fontWeight: 600,
              fontFamily: 'monospace',
              color: '#253E66',
              padding: '12px 0',
            }
          }}
          sx={{
            width: 56,
            '& .MuiOutlinedInput-root': {
              background: '#FFFFFF',
              borderRadius: '6px',
              '& fieldset': {
                borderColor: '#E5E5E5',
                borderWidth: 2,
              },
              '&:hover fieldset': {
                borderColor: '#D9DBDC',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#4698D3',
                borderWidth: 2,
              },
              '&.Mui-disabled fieldset': {
                borderColor: '#E5E5E5',
              },
            },
            '& .MuiInputBase-input': {
              color: '#253E66',
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#999999',
            },
          }}
        />
      ))}
    </Box>
  );
};

const ApplicationAdminLogin = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    username: 'app-admin',
    password: '',
    totpCode: ''
  });
  const [initFormData, setInitFormData] = useState({
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [appAdminStatus, setAppAdminStatus] = useState(null);
  const [showInitForm, setShowInitForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginStep, setLoginStep] = useState('password'); // 'password', 'mfa', 'setup'
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);

  useEffect(() => {
    checkAppAdminStatus();
  }, []);

  const checkAppAdminStatus = async () => {
    try {
      const response = await apiService.checkAppAdminStatus();
      if (response.success) {
        setAppAdminStatus(response.data);
        setShowInitForm(!response.data.initialized);
      }
    } catch (err) {
      console.error('App admin status check failed:', err);
      setAppAdminStatus({ initialized: false });
      setShowInitForm(true);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiService.appAdminLogin(
        formData.username,
        formData.password,
        formData.totpCode
      );

      if (response.success) {
        if (response.data.requiresMfaSetup) {
          setMfaSetupRequired(true);
          setMfaSetupData(response.data);
          setLoginStep('setup');
          setMfaRequired(false);
          toast.info('MFA setup is mandatory for application admin');
        } else if (response.data.token) {
          toast.success('Application admin login successful');
          setTimeout(() => {
            if (onLogin) {
              onLogin(response.data);
            }
          }, 1000);
        } else if (response.data.requiresMFA && !response.data.requiresMfaSetup) {
          setMfaRequired(true);
          setLoginStep('mfa');
          toast.success('Password verified. Please enter your TOTP code.');
        } else {
          toast.error('Unexpected login response');
        }
      }
    } catch (err) {
      toast.error(err.message || 'Login failed');
      setMfaRequired(false);
      setMfaSetupRequired(false);
      setLoginStep('password');
      setFormData(prev => ({ ...prev, totpCode: '' }));
    } finally {
      setLoading(false);
    }
  };

  const handleMFASetupComplete = async () => {
    toast.success('MFA setup completed successfully. You can now login.');
    setMfaSetupData(null);
    setMfaSetupRequired(false);
    setLoginStep('password');
    setFormData({ username: 'app-admin', password: '', totpCode: '' });
    await checkAppAdminStatus();
  };

  const resetToPasswordStep = () => {
    setLoginStep('password');
    setMfaRequired(false);
    setMfaSetupRequired(false);
    setFormData(prev => ({ ...prev, totpCode: '' }));
  };

  const handleInitAppAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiService.initializeAppAdmin(initFormData.password);

      if (response.success) {
        toast.success('Application admin initialized successfully');
        setInitFormData({ password: '' });
        await checkAppAdminStatus();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, isInit = false) => {
    const { name, value } = e.target;
    if (isInit) {
      setInitFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#F9FBFD',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              borderRadius: '12px',
              p: 4,
              width: 440,
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
            }}
          >
            {/* Header Section */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                <Avatar
                  sx={{
                    width: 72,
                    height: 72,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E5E5',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <img
                    src="/ck-auth-logo.png"
                    alt="CK Prism"
                    style={{
                      width: '80%',
                      height: '80%',
                      objectFit: 'contain'
                    }}
                  />
                </Avatar>
              </motion.div>

              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                  mb: 1,
                }}
              >
                Application Admin
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.9rem',
                }}
              >
                Manage customers and system settings
              </Typography>
            </Box>

            <AnimatePresence mode="wait">
              {/* MFA Setup Flow */}
              {loginStep === 'setup' && mfaSetupData && (
                <motion.div
                  key="mfa-setup"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <MFASetup
                    setupData={mfaSetupData}
                    onComplete={handleMFASetupComplete}
                    onError={(error) => toast.error(error)}
                  />
                </motion.div>
              )}

              {/* Admin Initialization */}
              {showInitForm && appAdminStatus && !appAdminStatus.initialized && loginStep === 'password' && (
                <motion.div
                  key="init-form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert
                    severity="info"
                    sx={{
                      mb: 3,
                      backgroundColor: 'rgba(70, 152, 211, 0.08)',
                      border: '1px solid rgba(70, 152, 211, 0.2)',
                      borderRadius: '8px',
                      '& .MuiAlert-icon': { color: '#4698D3' },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontFamily: 'Metropolis, sans-serif', color: '#253E66' }}>
                      Create the initial application admin account to manage customers.
                    </Typography>
                  </Alert>

                  <Box component="form" onSubmit={handleInitAppAdmin} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box>
                      <TextField
                        fullWidth
                        type="password"
                        label="Application Admin Password"
                        name="password"
                        value={initFormData.password}
                        onChange={(e) => handleInputChange(e, true)}
                        placeholder="Enter a strong password"
                        required
                        disabled={loading}
                        sx={inputStyles}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <KeyIcon sx={{ color: '#999999' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <PasswordStrengthIndicator password={initFormData.password} />
                    </Box>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading || !isPasswordValid(initFormData.password)}
                      sx={{
                        backgroundColor: '#4698D3',
                        borderRadius: '6px',
                        py: 1.5,
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: '0 2px 8px rgba(70, 152, 211, 0.25)',
                        '&:hover': {
                          backgroundColor: '#3580B8',
                          boxShadow: '0 4px 12px rgba(70, 152, 211, 0.35)',
                        },
                        '&:disabled': {
                          backgroundColor: '#E5E5E5',
                          color: '#999999',
                        },
                      }}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AdminIcon />}
                    >
                      {loading ? 'Initializing...' : 'Initialize Application Admin'}
                    </Button>
                  </Box>
                </motion.div>
              )}

              {/* Login Form */}
              {!showInitForm && loginStep !== 'setup' && (
                <motion.div
                  key="login-form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box component="form" onSubmit={handleLogin} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                      fullWidth
                      label="Username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                      disabled={loading || mfaRequired}
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountIcon sx={{ color: '#999999' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputStyles}
                    />

                    <TextField
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      label="Password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter your password"
                      required
                      disabled={loading || mfaRequired}
                      sx={inputStyles}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <KeyIcon sx={{ color: '#999999' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{ color: '#999999' }}
                              disabled={mfaRequired}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* MFA Step */}
                    <AnimatePresence>
                      {mfaRequired && loginStep === 'mfa' && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Box sx={{ py: 2 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: '#253E66',
                                fontFamily: 'Metropolis, sans-serif',
                                fontWeight: 600,
                                textAlign: 'center',
                                mb: 2,
                              }}
                            >
                              Enter TOTP Code
                            </Typography>

                            <TOTPInput
                              value={formData.totpCode}
                              onChange={(value) => setFormData(prev => ({ ...prev, totpCode: value }))}
                              disabled={loading}
                            />

                            <Typography
                              variant="caption"
                              sx={{
                                color: '#999999',
                                fontFamily: 'Metropolis, sans-serif',
                                fontSize: '0.75rem',
                                display: 'block',
                                textAlign: 'center',
                                mt: 2,
                              }}
                            >
                              Enter the 6-digit code from your authenticator app
                            </Typography>
                          </Box>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <Box sx={{ display: 'flex', gap: 2, flexDirection: mfaRequired ? 'row' : 'column' }}>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading || (!formData.password || (mfaRequired && formData.totpCode.length !== 6))}
                        sx={{
                          backgroundColor: '#4698D3',
                          borderRadius: '6px',
                          py: 1.5,
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          textTransform: 'none',
                          fontSize: '1rem',
                          boxShadow: '0 2px 8px rgba(70, 152, 211, 0.25)',
                          '&:hover': {
                            backgroundColor: '#3580B8',
                            boxShadow: '0 4px 12px rgba(70, 152, 211, 0.35)',
                          },
                          '&:disabled': {
                            backgroundColor: '#E5E5E5',
                            color: '#999999',
                          },
                        }}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                      >
                        {loading ? 'Logging in...' : (mfaRequired ? 'Complete Login' : 'Login')}
                      </Button>

                      {mfaRequired && (
                        <Button
                          variant="outlined"
                          onClick={resetToPasswordStep}
                          disabled={loading}
                          sx={{
                            borderColor: '#E5E5E5',
                            color: '#666666',
                            borderRadius: '6px',
                            py: 1.5,
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            textTransform: 'none',
                            minWidth: '100px',
                            '&:hover': {
                              borderColor: '#D9DBDC',
                              backgroundColor: '#F9FBFD',
                            },
                          }}
                        >
                          Back
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {appAdminStatus && !appAdminStatus.initialized && (
                    <Box sx={{ textAlign: 'center', mt: 3, pt: 3, borderTop: '1px solid #E5E5E5' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#999999',
                          mb: 1,
                          fontFamily: 'Metropolis, sans-serif',
                        }}
                      >
                        Need to initialize application admin?
                      </Typography>
                      <Button
                        variant="text"
                        onClick={() => setShowInitForm(true)}
                        sx={{
                          color: '#4698D3',
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: 'rgba(70, 152, 211, 0.08)',
                          },
                        }}
                      >
                        Initialize Application Admin
                      </Button>
                    </Box>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </Paper>
        </motion.div>
      </Box>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 9999 }}
      />
    </>
  );
};

export default ApplicationAdminLogin;
